/**
 * SidePanelContext.
 *
 * Exposes the side panel's current display mode (sidebar vs. maximized
 * modal) to descendants so they can adapt their layout — e.g. switching
 * to a two-column grid when the operator has expanded the panel to the
 * full-screen dialog where horizontal real estate is plentiful.
 *
 * Provided by `SidePanelLayout`. Consumed by `ApprovalCard` (and any
 * other panel content that wants to be layout-aware).
 */

import * as React from 'react'

export interface SidePanelContextValue {
  /**
   * True when the side panel is currently displayed as the maximized
   * modal dialog (large viewport). False when displayed as the
   * standard right-side drawer.
   */
  maximized: boolean
}

const SidePanelContext = React.createContext<SidePanelContextValue>({
  maximized: false,
})

export const SidePanelProvider = SidePanelContext.Provider

/**
 * Read the current side panel display mode.
 *
 * Safe to call outside a provider — defaults to `{ maximized: false }`
 * so legacy callers (and unit tests rendering panel content in
 * isolation) keep their sidebar-style layout.
 */
export function useSidePanelContext(): SidePanelContextValue {
  return React.useContext(SidePanelContext)
}
